import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OpraReportTemplate } from '@interfaces/report-templates.interface';
import { Checkbox } from '@progress/kendo-react-inputs';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import Button from '@components/core/buttons';
import { CustomDialog, DialogActions, DialogContent, DialogTitle } from '@components/core/dialog';
import { useTranslation } from '@hooks/index';
import NotificationService from '@utils/notification';
import { useSetTemplateVisibility } from '@apis/bff-api/report-templates';

import './styles.scss';

type FormInputs = {
    inUse: boolean;
};

const EditOPRAReportTemplateDialog = ({
    orgKey,
    currentReportTemplate,
    onClose,
}: {
    orgKey: string;
    currentReportTemplate: OpraReportTemplate | null;
    onClose: (shouldUpdate?: boolean | null) => void;
}) => {
    const isRetired = currentReportTemplate?.reportStatusName === 'Retired';
    const { t } = useTranslation();

    const { trigger: setTemplateVisibility, isMutating } = useSetTemplateVisibility();

    const formMethods = useForm<FormInputs>({
        mode: 'all',
        defaultValues: {
            inUse: !!currentReportTemplate?.inUse,
        },
    });
    const {
        control,
        formState: { isDirty },
        handleSubmit,
    } = formMethods;

    const onSubmit = (submitData: FormInputs) => {
        setTemplateVisibility({
            templateId: currentReportTemplate?.id,
            organizationKey: orgKey,
            visible: submitData.inUse,
        }).then(() => {
            NotificationService.showNotification(
                t('admin:reportTemplateSuccessfulUpdate', {
                    id: currentReportTemplate?._itemId || currentReportTemplate?.recordId,
                }),
            );
            onClose(true);
        });
    };

    return (
        <CustomDialog fullWidth open>
            <DialogTitle id="edit-report-template-title">
                {t(isRetired ? 'admin:editCustomReportTemplateRetired' : 'admin:editOPRAReportTemplateOrg')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    data-testid="close-dialog"
                    className="fa-lg close-icon-cv"
                    onClick={() => {
                        onClose(false);
                    }}
                />
            </DialogTitle>
            <form id="report-template-form" className="report-template-form">
                <DialogContent className={isRetired ? 'k-mb-2.5' : ''}>
                    {isRetired ? (
                        <span>{t('admin:templateWasRetired')}</span>
                    ) : (
                        <Controller
                            control={control}
                            name="inUse"
                            render={({ field: { onChange, value } }) => (
                                <div className="checkboxes-wrapper !k-mr-5 !k-mb-1">
                                    <div className="left-part">
                                        <Checkbox
                                            data-testid="inUse-checkbox"
                                            rounded={null}
                                            id="inUse-checkbox"
                                            className="cell-checkbox no-border"
                                            checked={value}
                                            onChange={onChange}
                                            label={t('admin:templateInUse')}
                                            size="medium"
                                        />
                                    </div>
                                </div>
                            )}
                        />
                    )}
                </DialogContent>
                {!isRetired && (
                    <DialogActions className="!k-px-5 k-mb-3 k-flex !k-justify-content-end">
                        <Button
                            disabled={!isDirty || isMutating}
                            text="update"
                            id="submit-report-template"
                            data-testid="submit-report-template"
                            type="button"
                            onClick={handleSubmit(onSubmit)}
                        />
                    </DialogActions>
                )}
            </form>
        </CustomDialog>
    );
};

export default EditOPRAReportTemplateDialog;
